import { useEffect, useState } from "react";
import { useBlocker } from "react-router-dom";
import ActionButton from "./action-button";
import ButtonsGroup from "./buttons-group";
import "./form-validating.css";
import Popup from "./popup";

export default function FormValidating(props: {
  action: (formData: FormData) => void;
  validate?: (formData: FormData) => string[];
  onReset?: () => void;
  className?: string;
  children: React.ReactNode;
  confirmNavigation?: boolean;
  onValidationErrors?: (errors: string[]) => void;
}): React.ReactNode {
  const [isDirty, setDirty] = useState(false);

  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      (props.confirmNavigation ?? true) &&
      isDirty &&
      currentLocation.pathname !== nextLocation.pathname
  );

  useEffect(() => {
    const handleBeforeUnload = (_event: BeforeUnloadEvent) => {
      // TODO: Handle page reload too...
      // if ((props.confirmNavigation ?? true) && isDirty) {
      //   event.preventDefault();
      // }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isDirty]);

  const getValidationErrors = (form: HTMLFormElement) => {
    const errorsList = props.validate?.(new FormData(form)) ?? [];

    if (!form.checkValidity()) {
      errorsList.push("HTML form is invalid.");
    }

    return errorsList;
  };

  const onChange = (event: React.ChangeEvent<HTMLFormElement>) => {
    setDirty(true);
    props.onValidationErrors?.(getValidationErrors(event.currentTarget));
  };

  const onSubmit = (event: React.SubmitEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;

    const errors = getValidationErrors(form);
    props.onValidationErrors?.(errors);

    if (errors.length > 0) {
      // We have errors
      form.reportValidity(); // TODO: remove this
      return;
    }

    // We are good to go
    const formData = new FormData(form);
    setDirty(false);
    props.action(formData);
  };

  const onReset = () => {
    setDirty(false);
    props.onReset?.();
  };

  return (
    <>
      <form
        className={`form-validating ${props.className ?? ""}`}
        onSubmit={onSubmit}
        onReset={onReset}
        onChange={onChange}
      >
        {props.children}
        {isDirty && <div className="technical">Changes detected</div>}
      </form>

      {blocker.state === "blocked" && (
        <Popup title="Unsaved Changes">
          <div>
            The form contains modifications. Are you sure you want to leave
            without saving your changes?
          </div>
          <ButtonsGroup>
            <ActionButton
              action="Continue Editing"
              onOk={() => blocker.reset()}
            />
            <ActionButton
              action="Leave"
              style="Alternate"
              onOk={() => {
                blocker.proceed();
              }}
            />
          </ButtonsGroup>
        </Popup>
      )}
    </>
  );
}
